import { useEffect, useState } from "react";
import { X, Package, AlertCircle, Loader2 } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { TrackingResult } from "@shared-core/data/api/tracking-types";
import { TrackingResultCard } from "./TrackingResultCard";

interface OrderTrackingModalProps {
  orderNo: string;
  onClose: () => void;
}

type State =
  | { type: "loading" }
  | { type: "results"; data: TrackingResult[] }
  | { type: "not_found" }
  | { type: "error" };

export function OrderTrackingModal({
  orderNo,
  onClose,
}: OrderTrackingModalProps) {
  const dataAdapter = useDataAdapter();
  const [state, setState] = useState<State>({ type: "loading" });

  useEffect(() => {
    let cancelled = false;
    dataAdapter
      .getOrderTracking(orderNo)
      .then((results) => {
        if (cancelled) return;
        setState(
          results.length === 0
            ? { type: "not_found" }
            : { type: "results", data: results },
        );
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const status = (err as { status?: number })?.status;
        setState(status === 404 ? { type: "not_found" } : { type: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [orderNo, dataAdapter]);

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-background border border-border rounded-lg shadow-lg w-full max-w-xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <h2 className="font-semibold">Delivery tracking</h2>
            <p className="text-sm text-muted-foreground">Order {orderNo}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded hover:bg-accent transition-colors"
            aria-label="Close"
          >
            <X className="size-4" />
          </button>
        </div>

        <div className="overflow-y-auto p-5 space-y-4">
          {state.type === "loading" && (
            <div className="flex items-center justify-center py-10 gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              Looking up tracking…
            </div>
          )}

          {state.type === "results" &&
            state.data.map((result) => (
              <TrackingResultCard
                key={result.tracking_reference}
                result={result}
              />
            ))}

          {state.type === "not_found" && (
            <div className="flex items-start gap-3 rounded-md border border-border bg-surface p-4 text-sm">
              <Package className="size-4 shrink-0 mt-0.5 text-muted-foreground" />
              <div>
                <p className="font-medium">No despatched consignments found</p>
                <p className="text-muted-foreground mt-0.5">
                  No consignments have been despatched for this order yet.
                </p>
              </div>
            </div>
          )}

          {state.type === "error" && (
            <div className="flex items-start gap-3 rounded-md border border-red-200 bg-red-50 p-4 text-sm">
              <AlertCircle className="size-4 shrink-0 mt-0.5 text-red-500" />
              <div>
                <p className="font-medium text-red-700">
                  Could not load tracking
                </p>
                <p className="text-red-600 mt-0.5">
                  Could not reach the courier API. Try again shortly.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
